import { FogExp2, PerspectiveCamera, Scene, Timer, Vector3 } from 'three';
import type { Object3D, Sphere } from 'three';
import { loadCards } from '../cards/cards';
import type { CardChanges, Cards } from '../cards/cards';
import { loadDifficulty, withDifficulty } from '../cards/difficulty';
import type { Level } from '../cards/difficulty';
import { loadShop } from '../cards/shop';
import type { ShopCard, ShopId } from '../cards/shop';
import { t } from '../i18n/i18n';
import { Controls } from '../input/controls';
import { isTyping } from '../input/typing';
import type { Fighter } from '../rules/fighter';
import type { FightCommand } from '../rules/types';
import type { TuningPanel } from '../tuning/panel';
import { buildHall } from '../view/arena/hall';
import { buildLights } from '../view/arena/lights';
import { buildRing } from '../view/arena/ring';
import { sceneTime } from '../view/arena/time';
import { BoxerAnimator } from '../view/boxer/animator';
import { approach, punchingHand } from '../view/boxer/motion';
import { balls, boxerBallCount, gloveRadius, placeBoxerBalls, placeBoxerFists } from '../view/bodies';
import type { PunchingArm } from '../view/collide';
import { buildBoxer } from '../view/boxer/boxer';
import type { BoxerRig } from '../view/boxer/boxer';
import { Hud } from '../view/hud/hud';
import { TurnPhone } from '../view/hud/phone';
import { Screens } from '../view/hud/screens';
import { CameraDirector } from '../view/camera/director';
import { FirstPerson } from '../view/player/firstPerson';
import { detectQuality, Pipeline } from '../view/render/pipeline';
import { FixedStep } from './loop';
import { buy, payout, ProgressStore, settle, TuningStore } from './progress';
import type { KeyValueStore, Progress } from './progress';
import { FightSession } from './session';
import { FightShow, knockoutShotSeconds } from './show';

const playerStands = new Vector3(0, 0, 0.82);
const opponentStands = new Vector3(0, 0, -0.52);
/** Where his punches aim, from the player's eyes. */
const chin = new Vector3(0, -0.1, 0);
// Low enough that his body blows come in under the player's guard.
const middle = new Vector3(0, -0.62, 0);
/** Where the player's punches land, from the middle of his skull and his chest: the front of his face and belly. */
const face = new Vector3(0, 0, 0.16);
const belly = new Vector3(0, 0.22, 0.2);
/** How long the round's name hangs before FIGHT!, at the start and at the end of each rest. */
const callSeconds = 1.6;
/** From the end of the bout to the result: long enough to watch a knockout. */
const resultDelaySeconds = knockoutShotSeconds + 0.2;
/** From the menu to the first bell: the camera cranes down from over the ring into the player's eyes. */
const craneSeconds = 2.6;

/** The whole game on one page: the arena, the two fighters, the bout, and the loop that runs them. */
export class Game {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(72, 1, 0.03, 120);
  readonly pipeline: Pipeline;
  cards: Cards;
  readonly boxer: BoxerRig;
  readonly animator: BoxerAnimator;
  readonly firstPerson: FirstPerson;
  readonly hud: Hud;
  readonly screens: Screens;
  private readonly turnPhone: TurnPhone;
  readonly show: FightShow;
  readonly director: CameraDirector;
  shop: ShopCard;
  readonly progress: Progress;
  session: FightSession;
  private mode: 'menu' | 'shop' | 'fight' = 'menu';
  private readonly store = new ProgressStore(browserStorage());
  private readonly tuning = new TuningStore(browserStorage());
  private readonly difficulty = loadDifficulty();
  /** Sashko's panel, loaded the first time he asks for it: most players never do. */
  private panel: Promise<TuningPanel> | null = null;
  private readonly controls: Controls;
  private readonly loop = new FixedStep(1 / 120);
  private readonly timer = new Timer();
  private readonly headAt = new Vector3();
  private readonly bodyAt = new Vector3();
  private readonly eyesAt = new Vector3();
  private readonly chinAt = new Vector3();
  private readonly middleAt = new Vector3();
  /** Both fighters as balls, for keeping gloves out of heads and bodies. */
  private readonly boxerBalls = balls(boxerBallCount);
  private readonly boxerFists = balls(2);
  private readonly playerHead = balls(1);
  private readonly playerFists = balls(2);
  /** Each side's arm on its way in a punch this frame, if any. */
  private readonly playerPunching: PunchingArm[] = [];
  private readonly boxerPunching: PunchingArm[] = [];
  private readonly playerArm = { wrist: new Vector3(), fist: new Vector3(), radius: gloveRadius };
  private readonly boxerArm = { wrist: new Vector3(), fist: new Vector3(), radius: gloveRadius };
  private readonly skull: Object3D;
  /** Seconds until the first round begins; nobody moves before. */
  private introLeft = 0;
  /** The round whose name was called during the rest before it. */
  private called = 1;
  /** Seconds since the bout ended, or -1 while it goes on. */
  private overFor = -1;
  /** The coins the last bout paid, or took. */
  private earned = 0;

  constructor(private readonly canvas: HTMLCanvasElement, hudRoot: HTMLElement, model: Object3D) {
    this.scene.fog = new FogExp2(0x0b0706, 0.045);
    const quality = detectQuality();
    this.pipeline = new Pipeline(canvas, this.scene, this.camera, quality);
    const changes = this.tuning.load();
    this.cards = cardsWith(changes);
    this.scene.add(buildRing(), buildHall(quality.crowd), buildLights(quality));

    this.boxer = buildBoxer(
      {
        skin: 0x664434,
        hair: 0x6b2f1a,
        gloves: { leather: 0xd21f25, trim: 0x2a0c0c, label: t('glove-label') },
        trunks: 0x1f6b3a,
        waistband: 0x1b1b1d,
        boots: 0x222226,
      },
      model,
    );
    this.boxer.root.position.copy(opponentStands);
    this.scene.add(this.boxer.root);
    this.skull = this.boxer.skull;
    this.animator = new BoxerAnimator(this.boxer);
    this.firstPerson = new FirstPerson(this.camera, playerStands);
    this.scene.add(this.firstPerson.body);

    this.hud = new Hud(hudRoot, this.cards.rules.superHits, t(this.cards.opponent.nameKey));
    this.hud.superPressed.add(() => this.submit({ hand: 'right', action: 'super' }));
    this.hud.againPressed.add(() => this.startFight());
    this.hud.menuPressed.add(() => this.toMenu());
    this.screens = new Screens(hudRoot);
    this.turnPhone = new TurnPhone(hudRoot);
    this.screens.fightPressed.add(() => this.startFight());
    this.screens.shopPressed.add(() => this.toShop());
    this.screens.backPressed.add(() => this.toMenu());
    this.screens.buyPressed.add((id) => this.buy(id));
    this.screens.levelPicked.add((id) => this.pickLevel(id));
    this.shop = shopWith(changes);
    this.progress = this.store.load();
    this.director = new CameraDirector(this.camera, this.firstPerson.armParts);
    const stage = { director: this.director, scene: this.scene, camera: this.camera, pipeline: this.pipeline, hud: this.hud, boxer: this.boxer, animator: this.animator, firstPerson: this.firstPerson, effects: this.cards.effects };
    this.show = new FightShow(stage, { head: this.headAt, body: this.bodyAt, skull: this.skull });
    this.session = this.newSession();
    this.toMenu();
    this.controls = new Controls(canvas, (command) => this.submit(command));
    // Browsers allow sound only after the player touches the page.
    const wake = () => this.show.sound.wake();
    window.addEventListener('pointerdown', wake);
    window.addEventListener('keydown', (event) => {
      wake();
      if (isTyping(event)) return;
      if (event.code === 'KeyM') this.show.sound.toggle();
      if (event.code === 'Backquote') void this.togglePanel();
    });
    window.addEventListener('resize', () => this.resize());
    this.resize();
    this.timer.connect(document);
  }

  /** Prepares the graphics card's programs for the whole scene without freezing the page while it compiles them. */
  async warmUp(): Promise<void> {
    await this.pipeline.renderer.compileAsync(this.scene, this.camera);
  }

  start(): void {
    this.pipeline.renderer.setAnimationLoop((time) => {
      this.timer.update(time);
      this.frame(Math.min(this.timer.getDelta(), 0.1));
    });
  }

  /** One frame: the rules in fixed steps, then everything shown as the rules have it. */
  frame(real: number): void {
    if (this.turnPhone.waiting) return;
    // A hit-stop or the knockout's slow motion bends the fighters' time; the crowd and the HUD keep the real one.
    const seconds = this.show.time.advance(real);
    sceneTime.value += real;
    this.controls.update();
    this.pace(seconds, real);
    const bout = this.session.bout;
    this.skull.getWorldPosition(this.headAt).add(face);
    this.boxer.chest.getWorldPosition(this.bodyAt).add(belly);
    this.camera.getWorldPosition(this.eyesAt);
    this.chinAt.copy(this.eyesAt).add(chin);
    this.middleAt.copy(this.eyesAt).add(middle);
    // The player keeps turned to face him as he moves about the ring.
    const body = this.firstPerson.body;
    const him = this.boxer.root.position;
    body.rotation.y = approach(body.rotation.y, Math.atan2(body.position.x - him.x, body.position.z - him.z), 6, seconds);
    // Each side's gloves stop on the other's body, and a punch on the other's gloves, as they stand now: his on the
    // player's from the last frame.
    this.firstPerson.placeBalls(this.playerHead[0], this.playerFists);
    punching(bout.player, this.firstPerson.gloveJoints, this.playerFists, this.playerArm, this.playerPunching);
    this.animator.update(seconds, bout.opponent, { head: this.chinAt, body: this.middleAt, covered: this.firstPerson.covered, parts: this.playerHead, gloves: this.playerFists, punching: this.playerPunching });
    placeBoxerBalls(this.boxer, this.boxerBalls);
    placeBoxerFists(this.boxer, this.boxerFists);
    punching(bout.opponent, [this.boxer.left.glove, this.boxer.right.glove], this.boxerFists, this.boxerArm, this.boxerPunching);
    this.firstPerson.update(seconds, bout.player, { head: this.headAt, body: this.bodyAt, parts: this.boxerBalls, gloves: this.boxerFists, punching: this.boxerPunching, covered: this.animator.covered });
    this.firstPerson.placeBalls(this.playerHead[0], this.playerFists);
    this.animator.settle();
    this.director.update(real);
    this.hud.setCinematic(this.director.filming);
    this.show.update(real, seconds, bout);
    this.hud.show(bout, real);
    this.pipeline.render(real);
  }

  submit(command: FightCommand): void {
    if (this.mode !== 'fight' || this.introLeft > 0) return;
    const answer = this.session.submit(command);
    if (answer === 'locked') this.hud.locked();
    if (answer === 'tired') this.hud.tired();
    if (answer === 'done' && command.action === 'dodge') this.show.dodged();
  }

  // The bout's own time, and the calls around it: the round's name before each round, FIGHT! as it starts, the result
  // a little after the end.
  private pace(seconds: number, real: number): void {
    const bout = this.session.bout;
    if (this.mode !== 'fight') return;
    if (this.introLeft > 0) {
      this.introLeft -= real;
      if (this.introLeft <= 0) this.show.fight();
      return;
    }
    this.loop.advance(seconds, (step) => this.session.step(step, this.controls.blockHeld));
    if (bout.phase === 'rest' && bout.secondsLeft <= callSeconds && this.called <= bout.round) {
      this.called = bout.round + 1;
      this.hud.announce(t('round-banner', this.called));
    }
    if (this.overFor >= 0 && this.overFor < resultDelaySeconds && (this.overFor += real) >= resultDelaySeconds) {
      this.hud.showResult(bout.result!, this.earned);
    }
  }

  // Between bouts: both fighters back in their corners, nobody moving but to breathe.
  private toMenu(): void {
    this.mode = 'menu';
    this.session = this.newSession();
    this.hud.setMode('menu');
    this.hud.hideResult();
    this.screens.showMenu(this.progress, this.difficulty.levels, this.shop);
  }

  private pickLevel(id: string): void {
    this.progress.level = id;
    this.store.save(this.progress);
    this.screens.showMenu(this.progress, this.difficulty.levels, this.shop);
  }

  private get level(): Level {
    return this.difficulty.levels.find((l) => l.id === this.progress.level)!;
  }

  private toShop(): void {
    this.mode = 'shop';
    this.hud.setMode('shop');
    this.screens.showShop(this.progress, this.shop);
  }

  private buy(id: ShopId): void {
    if (buy(this.progress, this.shop, id) !== 'bought') return;
    this.store.save(this.progress);
    this.screens.showShop(this.progress, this.shop);
  }

  private async togglePanel(): Promise<void> {
    this.panel ??= import('../tuning/panel').then(({ TuningPanel }) => new TuningPanel(this.tuning.load(), (changed) => this.retune(changed)));
    (await this.panel).toggle();
  }

  // Sashko moved a slider: saved at once, in the game from the next bout.
  private retune(changes: CardChanges): void {
    this.tuning.save(changes);
    this.cards = cardsWith(changes);
    this.shop = shopWith(changes);
    this.show.setEffects(this.cards.effects);
    if (this.mode === 'shop') this.screens.showShop(this.progress, this.shop);
    if (this.mode === 'menu') this.screens.showMenu(this.progress, this.difficulty.levels, this.shop);
  }

  private startFight(): void {
    this.mode = 'fight';
    this.session = this.newSession();
    this.show.watch(this.session.bout);
    // The coins are the player's the moment the bout ends, before the result is shown.
    this.session.bout.over.add((result) => {
      this.overFor = 0;
      this.earned = settle(this.progress, payout(result, this.shop, this.level));
      this.store.save(this.progress);
    });
    this.introLeft = craneSeconds;
    this.director.craneIn(this.skull.getWorldPosition(new Vector3()).add(face), craneSeconds);
    this.screens.hide();
    this.hud.setMode('fight');
    this.hud.hideResult();
    this.hud.setSuperHits(this.cards.rules.superHits);
    this.hud.setSuperOwned(this.progress.owned.has('super'));
    this.hud.announce(t('round-banner', 1));
  }

  private newSession(): FightSession {
    const session = new FightSession(withDifficulty(this.cards, this.level), (id) => this.progress.owned.has(id));
    this.animator.reset();
    this.firstPerson.reset();
    this.introLeft = 0;
    this.called = 1;
    this.overFor = -1;
    return session;
  }

  private resize(): void {
    this.pipeline.resize(this.canvas.clientWidth || window.innerWidth, this.canvas.clientHeight || window.innerHeight);
  }
}

// The fighter's arm on its way in a punch, if any, from where his wrists and fists (left, right) are now.
function punching(fighter: Fighter, wrists: readonly Object3D[], fists: readonly Sphere[], arm: PunchingArm & { wrist: Vector3; fist: Vector3 }, out: PunchingArm[]): void {
  const hand = punchingHand(fighter);
  out.length = 0;
  if (!hand) return;
  const i = hand === 'left' ? 0 : 1;
  wrists[i].getWorldPosition(arm.wrist);
  arm.fist.copy(fists[i].center);
  out.push(arm);
}

// The browser's storage for the save; some browsers refuse even to hand it over, and then nothing is kept.
function browserStorage(): KeyValueStore {
  try {
    return window.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined };
  }
}

// Sashko's changes on the cards; if they no longer make a card the rules accept, the cards as they were made.
function cardsWith(changes: CardChanges): Cards {
  try {
    return loadCards(changes);
  } catch {
    return loadCards();
  }
}

function shopWith(changes: CardChanges): ShopCard {
  try {
    return loadShop(changes.shop);
  } catch {
    return loadShop();
  }
}
